<template>
    <div class="nft-search" v-bind:class="{ 'nft-search--open': isOpen }">
        <!-- Desktop: the field is always out. Tablet and phone: the tab strip has
             no room for it, so it folds into the loupe and unfolds on a tap, the
             way the loupe in the site header unfolds its search. Unfolded, the
             field looks like the header one: the loupe sits inside it. -->
        <label class="nft-search__field" v-show="isExpanded">
            <icon-search class="nft-search__loupe"/>
            <input class="nft-search__input" type="search" ref="input"
                v-bind:value="query"
                v-bind:placeholder="$t('address.nfts.search_placeholder')"
                v-bind:aria-label="$t('address.nfts.search_placeholder')"
                v-on:input="setQuery($event.target.value)"
                v-on:keydown.esc="close"
                v-on:blur="collapseIfEmpty"/>
            <button type="button" class="nft-search__clear ui-search-clear" v-if="query"
                v-on:mousedown.prevent
                v-on:click="clear">
                <icon-close/>
            </button>
        </label>

        <!-- Unfolded, the loupe button stays in the flow but invisible, so the
             field can take its place and the header keeps its height: -->
        <div class="nft-search__button" tabindex="0" v-show="isCollapsible"
            v-bind:class="{ 'nft-search__button--hidden': isOpen }"
            v-on:click="open"
            v-on:keyup.enter="open">
            <div class="nft-search__icon">
                <icon-search/>
            </div>
        </div>
    </div>
</template>

<script>
import IconSearch from '@img/icons/lucide/search.svg?vue';
import IconClose from '@img/icons/lucide/x.svg?vue';

export default {
    data() {
        return {
            isOpen: false,
        };
    },

    computed: {
        query() {
            return this.$store.state.nftSearchQuery;
        },

        isCollapsible() {
            return this.isMobile || this.isTablet;
        },

        isExpanded() {
            return !this.isCollapsible || this.isOpen;
        },
    },

    // The header is shared by every tab, so the query must not outlive the
    // NFT tab it was typed for: leaving it (or the page) clears the search.
    beforeDestroy() {
        this.setQuery('');
    },

    methods: {
        setQuery(value) {
            this.$store.commit('updateNftSearchQuery', value.trim());
        },

        // The loupe button exists only while the field is folded (tablet and phone):
        open() {
            this.isOpen = true;
            this.$nextTick(() => this.$refs.input.focus());
        },

        close() {
            this.setQuery('');
            this.isOpen = false;
        },

        clear() {
            this.setQuery('');
            this.$refs.input.focus();
        },

        // An empty field folds back into the loupe once it loses focus, a
        // field with a query stays -- the grid is showing its results.
        collapseIfEmpty() {
            if (!this.query) {
                this.isOpen = false;
            }
        },
    },

    components: {
        IconSearch,
        IconClose,
    },
};
</script>

<style lang="scss">
// The folded loupe repeats .nft-spam-toggle: the two sit side by side in the
// tab header and have to line up. The unfolded field repeats the site header
// search (.header-search .search-input), minus its grey fill and hotkey badge.
.nft-search {
    display: flex;
    align-items: center;
    padding: 0 4px;
    position: relative;
    z-index: 90;
    &__button {
        display: flex;
        align-items: center;
        position: relative;
        border-radius: 8px;
        padding: 0 8px;
        line-height: 36px;
        user-select: none;
        cursor: pointer;
        &:hover .nft-search__icon {
            color: var(--body-text-color);
        }
        &--hidden {
            visibility: hidden;
            pointer-events: none;
        }
    }
    &__icon {
        display: flex;
        align-items: center;
        width: 18px;
        height: 36px;
        color: var(--body-muted-text-color);
        fill: currentColor;
        svg {
            width: 100%;
            height: 100%;
        }
    }
    &__field {
        display: flex;
        align-items: center;
        width: 280px;
        height: 38px;
        padding: 0 8px 0 12px;
        box-sizing: border-box;
        border-radius: 12px;
        border: 1px solid var(--card-border-color);
        background: var(--card-background);
        color: var(--body-text-color);
        cursor: text;
        transition: border-color .15s ease, box-shadow .15s ease;
        &:focus-within {
            border-color: var(--blue-bright);
            box-shadow: 0 0 0 3px var(--blue-bright-opacity);
        }
    }
    &__loupe {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        margin-right: -2px;
        opacity: .55;
        stroke: currentColor;
        fill: none;
    }
    &__input {
        flex: 1;
        min-width: 0;
        height: 100%;
        padding: 0 12px;
        border: none;
        outline: none;
        background: transparent;
        color: inherit;
        font-size: 14px;
        font-family: inherit;
        text-overflow: ellipsis;
        &::placeholder {
            color: var(--body-muted-text-color);
        }
        // The browser's own clear cross: the field is small and it would sit askew in it.
        &::-webkit-search-cancel-button {
            -webkit-appearance: none;
        }
    }
}

// Unfolded on a tablet or a phone the field lies over the tab strip rather
// than squeezing it, from the left edge of the header to where the loupe was:
@media screen and (max-width: 991px) {
    .nft-search__field {
        position: absolute;
        top: 50%;
        right: 4px;
        transform: translateY(-50%);
        width: calc(100vw - 112px);
        max-width: 420px;
        z-index: 2;
        box-shadow: 0 0 0 6px var(--card-header-background);
        &:focus-within {
            box-shadow: 0 0 0 3px var(--blue-bright-opacity), 0 0 0 6px var(--card-header-background);
        }
    }
}
</style>
